/**
 * @file DynamicFormContext.tsx
 * @description Global context for opening dynamic Form Builder forms in a modal.
 *
 * Provides `openDynamicForm()` which resolves the form schema via the entity
 * form resolution API and renders it in an AppModal. Callers supply an
 * `onSubmit(formData)` callback to handle the domain-specific submission
 * (e.g. commitUtilization, createCase, createClaim).
 *
 * Usage:
 *   const { openDynamicForm } = useDynamicForm();
 *   openDynamicForm({
 *     programId,
 *     entityType: 'UTILIZATION',
 *     actionType: 'CREATE',
 *     title: 'Record Utilization',
 *     onSubmit: async (formData) => { await commitUtilization(formData); },
 *   });
 */

import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import type { EntityFormType, EntityFormActionType, FormContextType } from '../types/entityFormResolution';
import type { FieldValue } from '../components/workspace/dynamicFormFieldUtils';

// ── Public API ──────────────────────────────────────────────────────────────

export interface DynamicFormRequest {
  programId: string;
  entityType: EntityFormType;
  actionType?: EntityFormActionType;
  contextType?: FormContextType;
  workflowStepId?: string;
  title?: string;
  subtitle?: string;
  /** Pre-fill values keyed by field internalName */
  prefill?: Record<string, FieldValue>;
  /** Custom submit handler. Receives the validated formData. */
  onSubmit: (formData: Record<string, FieldValue>) => Promise<void>;
  /** Called after successful submit + modal close. */
  onSuccess?: () => void;
  /** Called on submit error. */
  onError?: (error: unknown) => void;
  /** Modal size override */
  size?: 'default' | 'lg' | 'xl';
}

interface DynamicFormContextValue {
  /** Opens a dynamic form modal */
  openDynamicForm: (request: DynamicFormRequest) => void;
  /** Closes the currently open form modal */
  closeDynamicForm: () => void;
  /** Whether a form modal is currently open */
  isOpen: boolean;
  /** The active form request (if open) */
  activeRequest: DynamicFormRequest | null;
}

// ── Context ─────────────────────────────────────────────────────────────────

const DynamicFormContext = createContext<DynamicFormContextValue | null>(null);

export function DynamicFormProvider({ children }: { children: ReactNode }) {
  const [activeRequest, setActiveRequest] = useState<DynamicFormRequest | null>(null);

  const openDynamicForm = useCallback((request: DynamicFormRequest) => {
    setActiveRequest(request);
  }, []);

  const closeDynamicForm = useCallback(() => {
    setActiveRequest(null);
  }, []);

  return (
    <DynamicFormContext.Provider
      value={{
        openDynamicForm,
        closeDynamicForm,
        isOpen: activeRequest !== null,
        activeRequest,
      }}
    >
      {children}
    </DynamicFormContext.Provider>
  );
}

export function useDynamicForm(): DynamicFormContextValue {
  const ctx = useContext(DynamicFormContext);
  if (!ctx) {
    throw new Error('useDynamicForm must be used within a DynamicFormProvider');
  }
  return ctx;
}
